import { useState } from 'react';
import { useRateAiMessage } from '@/api/ai-conversations/ai-conversations.hooks';
import type { AssistantMessageRating } from '@/api/ai/ai.types';

interface AssistantRatingProps {
  /** Persisted assistant message id (from `metadata.messageId`). */
  messageId: string;
  /** Persisted conversation id, used to invalidate the cached detail on save. */
  conversationId: string | null;
  /** Initial rating value as hydrated from the server / streamed metadata. */
  initialRating: AssistantMessageRating;
}

/**
 * Compact thumbs-up / thumbs-down control rendered inside the `TurnFooter`
 * for each completed assistant turn. The state is optimistic — the buttons
 * toggle locally on click, then `POST /v1/ai/messages/:id/rating` writes the
 * value back. A second click of the same direction clears the rating.
 *
 * The local state is keyed on `messageId` (parent must pass `key={messageId}`
 * — `TurnFooter` does), so switching to a different conversation rehydrates
 * the rating from the new server value without an effect.
 */
export function AssistantRating({
  messageId,
  conversationId,
  initialRating,
}: AssistantRatingProps) {
  const [rating, setRating] = useState<AssistantMessageRating>(initialRating);
  const mutation = useRateAiMessage();

  const submit = (next: AssistantMessageRating) => {
    if (mutation.isPending) return;
    const previous = rating;
    setRating(next);
    mutation.mutate(
      { messageId, rating: next, conversationId: conversationId ?? undefined },
      {
        onError: () => setRating(previous),
      },
    );
  };

  const toggle = (target: 'up' | 'down') => {
    submit(rating === target ? null : target);
  };

  const disabled = !messageId || mutation.isPending;

  return (
    <span
      className="eos-asst-rating"
      role="group"
      aria-label="Diese Antwort bewerten"
    >
      <button
        type="button"
        className={`eos-asst-rating-btn${rating === 'up' ? ' is-on' : ''}`}
        aria-pressed={rating === 'up'}
        aria-label="Hilfreich"
        title="Hilfreich"
        disabled={disabled}
        onClick={() => toggle('up')}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M5.5 13.5V6.6l3-4.1c.5-.7 1.5-.4 1.5.5v3h2.5c.8 0 1.4.7 1.2 1.5l-1 4.5c-.1.6-.7 1.1-1.3 1.1H5.5zM2.5 6.5h2.5v7H2.5z"
            fill="currentColor"
          />
        </svg>
      </button>
      <button
        type="button"
        className={`eos-asst-rating-btn${rating === 'down' ? ' is-on' : ''}`}
        aria-pressed={rating === 'down'}
        aria-label="Nicht hilfreich"
        title="Nicht hilfreich"
        disabled={disabled}
        onClick={() => toggle('down')}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path
            d="M10.5 2.5v6.9l-3 4.1c-.5.7-1.5.4-1.5-.5v-3H3.5c-.8 0-1.4-.7-1.2-1.5l1-4.5c.1-.6.7-1.1 1.3-1.1H10.5zM13.5 2.5H11v7h2.5z"
            fill="currentColor"
          />
        </svg>
      </button>
    </span>
  );
}
